import { assert, definedMap } from "@glideapps/ts-necessities";
import { RootEvalEnvironment } from "common/components/computation-types";
import { Constant } from "common/components/data";
import type { DeserializedRootScreen, EvaluablePivot } from "common/components/deserialization";
import { NamedScreenTemplate, deserializeEvaluable, deserializeMenuItem } from "common/components/deserialization";
import type { AppDescription, SerializedApp } from "common/components/SerializedApp";
import {
    AppKind,
    ScreenFlag,
    defaultAppFeatures,
    firstNonHiddenNonFlyoutTabIndex,
    getAppFeatures,
    getAppKind,
    getAppTabs,
} from "common/components/SerializedApp";
import type {
    AppEnvironment,
    AppOther,
    Context,
    Evaluable,
    EvaluatedScreen,
    MinimalAppEnvironment,
    ValueProducer,
} from "common/components/types";
import { Data } from "common/components/types";
import { ColumnUI } from "common/components/uis";
import type { BuilderActionsForApp } from "common/Database/builder-actions";
import type { TabDescription, TypeSchema } from "common/description";
import { buildSerializedApp } from "generator/build-serialized-app";
import { BuilderAppBuilder } from "generator/builder";
import { getScreenProperty } from "generator/components/property-utils";
import { makeSimpleAppDescriptionContext } from "generator/components/simple-ccc";
import type { MaterialGlideAppState } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import type { AppNavigationModel } from "webapp/lib/navigation-model";
import { createNavigationModel } from "webapp/lib/navigation-model";

import { ForwardingAppEnvironment } from "./forwarding-app-environment";
import { glideAppStateFromProps } from "./glide-app-state-from-props";

const dummyContext = new Data<AppOther, AppEnvironment>({});
const dummyScreen: EvaluatedScreen = {
    title: undefined,
    ui: new ColumnUI("column", new Constant<AppOther, AppEnvironment>([])),
    inputCtx: dummyContext,
    ctx: dummyContext,
    name: undefined,
    topBar: undefined,
    pivotPicker: undefined,
    quotaBannerMessage: new Constant<AppOther, AppEnvironment>(undefined),
    menuItems: [],
    flags: new Set(),
    screenId: "DUMMY",
    itemID: undefined,
    needsRerender: false,
};

const dummyGlideAppState: MaterialGlideAppState = {
    rootContext: dummyContext,
    functions: new Map(),
    screens: new Map(),
    computedColumns: new Map(),
    navigationModel: createDummyNavigationModel(),
};

export function buildAndEvaluateScreen(
    screenName: string,
    serializedApp: SerializedApp,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema,
    inputCtx: Context,
    pushedTitle: Evaluable | undefined,
    appEnvironment: MinimalAppEnvironment,
    userDefinedComponents: ReadonlyMap<string, Evaluable>,
    additionalScreenFlags: ReadonlySet<ScreenFlag> | undefined,
    screenID: string | undefined,
    itemID: string | undefined,
    tab: TabDescription | undefined,
    rebuildInputContext: boolean
): {
    pivots: readonly EvaluatedScreen[];
    userDefinedComponents: ReadonlyMap<string, Evaluable>;
    isVisible: ValueProducer | undefined;
} {
    if (getAppKind(serializedApp) === AppKind.Page) {
        return {
            pivots: [
                // The old navigation model depends on having at least one
                // pivot in the screen.
                dummyScreen,
            ],
            userDefinedComponents: new Map(),
            isVisible: undefined,
        };
    }
    assert(appEnvironment instanceof ForwardingAppEnvironment);

    const features = getAppFeatures(serializedApp);
    const adc = makeSimpleAppDescriptionContext(
        appEnvironment.appID,
        getAppKind(serializedApp),
        serializedApp,
        builderActions,
        schema,
        features,
        appEnvironment.eminenceFlags
    );
    const appBuilder = new BuilderAppBuilder(adc, features, appEnvironment.eminenceFlags);
    const {
        pivotComponents,
        additionalComponents,
        ctxModifier,
        title,
        topBar,
        pivotPicker,
        quotaBannerMessage,
        flags,
    } = appBuilder.makeScreen(screenName);

    const newUserDefinedComponents = new Map(userDefinedComponents);
    if (additionalComponents !== undefined) {
        for (const [name, component] of additionalComponents.entries()) {
            newUserDefinedComponents.set(name, deserializeEvaluable(component));
        }
    }

    const env = new RootEvalEnvironment(appEnvironment, newUserDefinedComponents);

    let isVisible: ValueProducer | undefined;
    if (tab !== undefined) {
        isVisible = definedMap(
            appBuilder.compileTabVisibilityFilters(tab.visibilityFilters),
            deserializeEvaluable
        )?.evalInEnvironment(env, inputCtx);

        if (rebuildInputContext) {
            const screenContext = appBuilder.makeScreenContextForTab(tab);
            if (screenContext !== undefined) {
                inputCtx = deserializeEvaluable(screenContext).evalInEnvironment(env, inputCtx);
            }
        }
    }

    const evaluableCtxModifier = definedMap(ctxModifier, deserializeEvaluable);
    const evaluableTitle = definedMap(title, deserializeEvaluable);
    const evaluableTopBar = definedMap(topBar, deserializeEvaluable);
    const evaluablePivotPicker = definedMap(pivotPicker, deserializeEvaluable);
    const evaluatedQuotaBannerMessage = deserializeEvaluable(quotaBannerMessage);

    const pivots: EvaluablePivot[] = pivotComponents.map(p => ({
        componentName: p.componentName,
        component: deserializeEvaluable(p.component),
        menuItems: p.menuItems.map(deserializeMenuItem),
    }));

    const template = new NamedScreenTemplate(
        pivots,
        evaluableCtxModifier,
        evaluableTitle,
        evaluableTopBar,
        evaluablePivotPicker,
        evaluatedQuotaBannerMessage,
        flags,
        serializedApp.screenDescriptions[screenName]
    );

    return {
        pivots: template.instantiate(
            inputCtx,
            evaluableTitle ?? pushedTitle,
            env,
            additionalScreenFlags,
            screenID,
            itemID
        ),
        userDefinedComponents: newUserDefinedComponents,
        isVisible,
    };
}

function createDummyNavigationModel(): AppNavigationModel {
    return createNavigationModel(
        [
            {
                pivots: [dummyScreen],
                isVisible: undefined,
            },
        ],
        0,
        {}
    );
}

export function evaluateApp(
    appEnvironment: MinimalAppEnvironment,
    serializedApp: SerializedApp,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema
): MaterialGlideAppState {
    if (getAppKind(serializedApp) === AppKind.Page) {
        return dummyGlideAppState;
    }

    assert(appEnvironment instanceof ForwardingAppEnvironment);

    const tabs = getAppTabs(serializedApp);
    let rootContext: Context | undefined;
    const makeRootScreens = (
        _screens: readonly DeserializedRootScreen[],
        functions: ReadonlyMap<string, Evaluable>
    ): { screens: readonly DeserializedRootScreen[]; functions: ReadonlyMap<string, Evaluable> } => {
        rootContext = new Data<AppOther, AppEnvironment>({});

        const screens: DeserializedRootScreen[] = [];
        for (const t of tabs) {
            const screenName = getScreenProperty(t.screenName);
            if (screenName === undefined) {
                screens.push({ pivots: [], isVisible: undefined });
                continue;
            }
            const { pivots, isVisible, userDefinedComponents } = buildAndEvaluateScreen(
                screenName,
                serializedApp,
                builderActions,
                schema,
                rootContext,
                new Data<AppOther, AppEnvironment>(t.title),
                appEnvironment,
                functions,
                new Set([ScreenFlag.IsTopScreen]),
                undefined,
                undefined,
                t,
                true
            );
            screens.push({ pivots, isVisible });
            functions = userDefinedComponents;
        }
        return { screens, functions };
    };

    const { state, evaluatedRoots } = glideAppStateFromProps(serializedApp, appEnvironment, makeRootScreens);
    let navigationModel: AppNavigationModel;
    if (evaluatedRoots !== undefined) {
        navigationModel = createNavigationModel(evaluatedRoots, firstNonHiddenNonFlyoutTabIndex(tabs), {});
    } else {
        navigationModel = createDummyNavigationModel();
    }
    return {
        ...state,
        rootContext: rootContext ?? dummyContext,
        navigationModel,
    };
}

// This is only used in the builder
export function buildAndEvaluateApp(
    appEnvironment: MinimalAppEnvironment | ForwardingAppEnvironment,
    schema: TypeSchema,
    appDesc: AppDescription,
    builderActions: BuilderActionsForApp
): { serializedApp: SerializedApp; appState: MaterialGlideAppState } {
    if (getAppKind(appDesc) === AppKind.Page) {
        return {
            serializedApp: {
                ...appDesc,
                root: {
                    tabs: [{}],
                    ctx: { $kind: "Empty" },
                },
                screens: {},
                functions: undefined,
                computedColumns: undefined,
                builderActions: undefined,
                features: { ...defaultAppFeatures, ...getAppFeatures(appDesc) },
            },
            appState: dummyGlideAppState,
        };
    }

    assert(appEnvironment instanceof ForwardingAppEnvironment);
    const serializedApp = buildSerializedApp(appEnvironment, schema, appDesc, builderActions);
    // FIXME: This will rebuild screens we already built in the previous line
    const appState = evaluateApp(appEnvironment, serializedApp, builderActions, schema);

    return { serializedApp, appState };
}

export function makeContextForClassScreen(row: ValueProducer): Context {
    return new Data<AppOther, AppEnvironment>({ data: row });
}

export function makeContextForArrayScreen(rows: ValueProducer): Context {
    return new Data<AppOther, AppEnvironment>({ data: rows });
}
